import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getUserList, updateUserGrants } from "../../api/users/grant";

// 유저 정보를 표시하는 카드 컴포넌트
const UserCard = ({ user, onToggle }) => (
  <div className="section-card p-3 space-y-3">
    <div>
      <p className="font-semibold text-gray-800">{user.name}</p>
      <p className="text-xs text-gray-500">{user.email}</p>
    </div>
    <div className="border-t pt-3 space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm">관리자 권한</span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={user.is_staff}
            onChange={(e) => onToggle(user.id, "is_staff", e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-sm">계정 활성화</span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={user.is_active}
            onChange={(e) => onToggle(user.id, "is_active", e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
        </label>
      </div>
    </div>
  </div>
);

// 필터링 UI 컴포넌트
const FilterControls = ({ filters, onChange, onToggle, isOpen }) => {
  if (!isOpen) return null;

  return (
    <div className="section-card p-3 space-y-3 mb-4 animate-fade-in-soft">
      <input
        type="text"
        placeholder="이름 검색"
        value={filters.name}
        onChange={(e) => onChange("name", e.target.value)}
        className="w-full border px-3 py-2 rounded-md bg-white text-sm"
      />
      <input
        type="text"
        placeholder="이메일 검색"
        value={filters.email}
        onChange={(e) => onChange("email", e.target.value)}
        className="w-full border px-3 py-2 rounded-md bg-white text-sm"
      />
      <select
        value={filters.is_staff}
        onChange={(e) => onChange("is_staff", e.target.value)}
        className="w-full border px-3 py-2 rounded-md bg-white text-sm"
      >
        <option value="all">관리자 전체</option>
        <option value="true">관리자만</option>
        <option value="false">일반 사용자</option>
      </select>
      <select
        value={filters.is_active}
        onChange={(e) => onChange("is_active", e.target.value)}
        className="w-full border px-3 py-2 rounded-md bg-white text-sm"
      >
        <option value="all">활성 전체</option>
        <option value="true">활성 계정</option>
        <option value="false">비활성 계정</option>
      </select>
    </div>
  );
};

export default function MobileUserGrantList() {
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  const [pendingChanges, setPendingChanges] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchFilters, setSearchFilters] = useState({
    name: "",
    email: "",
    is_staff: "all",
    is_active: "all",
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getUserList();
      const list = res.data?.results ?? res.data ?? [];
      setOriginalRows(list);
      setPendingChanges({});
    } catch (e) {
      console.error("사용자 목록 로드 실패", e);
      alert("사용자 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGrantToggle = useCallback(
    (userId, key, value) => {
      setRows((prevRows) =>
        prevRows.map((u) => (u.id === userId ? { ...u, [key]: value } : u))
      );
      setPendingChanges((prev) => {
        const currentChanges = prev[userId] || {};
        const newChanges = { ...currentChanges, [key]: value };
        const originalRow = originalRows.find((r) => r.id === userId);
        if (originalRow && originalRow[key] === value) {
          delete newChanges[key];
        }
        if (Object.keys(newChanges).length === 0) {
          const { [userId]: removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [userId]: newChanges };
      });
    },
    [originalRows]
  );

  const handleSave = useCallback(async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    setLoading(true);
    try {
      await Promise.all(
        Object.entries(pendingChanges).map(([userId, data]) =>
          updateUserGrants(parseInt(userId), data)
        )
      );
      alert("모든 권한 변경 사항이 성공적으로 저장되었습니다.");
      await loadUsers();
    } catch (e) {
      console.error("일부 권한 저장 실패:", e);
      alert(
        `일부 권한 수정에 실패했습니다: ${
          e.message || e
        }. 데이터를 다시 불러옵니다.`
      );
      await loadUsers();
    } finally {
      setLoading(false);
    }
  }, [pendingChanges, loadUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredRows = useMemo(() => {
    return originalRows.filter((user) => {
      const nameMatch = user.name
        ?.toLowerCase()
        .includes(searchFilters.name.toLowerCase());
      const emailMatch = user.email
        ?.toLowerCase()
        .includes(searchFilters.email.toLowerCase());
      const staffMatch =
        searchFilters.is_staff === "all" ||
        user.is_staff === (searchFilters.is_staff === "true");
      const activeMatch =
        searchFilters.is_active === "all" ||
        user.is_active === (searchFilters.is_active === "true");
      return nameMatch && emailMatch && staffMatch && activeMatch;
    });
  }, [searchFilters, originalRows]);

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-4 mobile-bottom-spacing pb-24">
        <div className="mobile-page-header flex justify-between items-center">
          <h1 className="mobile-page-title">사용자 권한 관리</h1>
          <button
            onClick={() => setIsFilterOpen((v) => !v)}
            className="text-sm text-blue-600 font-medium"
          >
            {isFilterOpen ? "필터 닫기" : "필터 열기"}
          </button>
        </div>

        <FilterControls
          filters={searchFilters}
          onChange={(key, value) =>
            setSearchFilters((prev) => ({ ...prev, [key]: value }))
          }
          isOpen={isFilterOpen}
        />

        {loading ? (
          <div className="text-center py-10">로딩 중...</div>
        ) : filteredRows.length > 0 ? (
          filteredRows.map((user) => (
            <UserCard key={user.id} user={user} onToggle={handleGrantToggle} />
          ))
        ) : (
          <div className="text-center py-10 text-sm text-gray-500">
            표시할 사용자가 없습니다.
          </div>
        )}
      </div>

      {Object.keys(pendingChanges).length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-3 bg-white border-t shadow-lg bottom-nav-mobile-inner">
           <div className="max-w-md mx-auto">
            <button
              className="w-full px-4 py-3 text-white rounded-lg shadow-md bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400"
              onClick={handleSave}
              disabled={loading}
            >
              {loading
                ? "저장 중..."
                : `변경 사항 저장 (${Object.keys(pendingChanges).length})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
